/**
 * Persistent per-mission results, stored in localStorage and keyed by mission
 * code (see SimConfig `SCENARIOS[].code`, e.g. 'CB-001').
 *
 * Combat scenarios are the only ones that produce a {@link MissionStats}
 * result, so this only ever holds combat missions. App writes a result through
 * {@link record} when a mission ends; the start screen reads back the best
 * grade per mission via {@link getGrade} to badge the mission list.
 *
 * The stored map is a plain object `{ [code]: MissionRecord }`:
 *
 *   MissionRecord = {
 *     code,                 // mission code (redundant with the key, for clarity)
 *     plays,                // total times ended (complete or failed)
 *     completions,          // times ended with outcome 'complete'
 *     best,                 // best-scoring result (see MissionResult)
 *     last,                 // most recent result
 *   }
 *   MissionResult = {
 *     score, grade, outcome,  // from MissionStats.toResult()
 *     stats: { ... },         // serialized stat snapshot
 *     at,                     // ISO timestamp of the run
 *   }
 *
 * All reads/writes are defensive: a disabled, full, or corrupt store degrades
 * to "no saved data" rather than throwing into the game loop.
 */

// Versioned so the schema can change later without colliding with old data.
const STORAGE_KEY = 'flightSim.missionRecords.v1';

/** Read the full record map. Returns an empty object on any failure. */
export function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function saveAll(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage disabled (private mode) or full — persistence is best-effort.
  }
}

/**
 * Merge a finished mission's result into the store, keeping the best-scoring
 * run as `best`. `result` is a MissionResult-shaped object (see MissionStats
 * `toResult`); `at` is stamped here. Returns the updated record.
 *
 * @param {string} code
 * @param {{ score: number, grade: string, outcome: string, stats: object }} result
 */
export function record(code, result) {
  if (!code || !result) return null;
  const map = loadAll();
  const entry = { ...result, at: new Date().toISOString() };
  const prev = map[code];

  const plays = (prev?.plays || 0) + 1;
  const completions = (prev?.completions || 0) + (result.outcome === 'complete' ? 1 : 0);
  // Ties keep the earlier best (strictly-greater wins).
  const best = !prev?.best || entry.score > prev.best.score ? entry : prev.best;

  map[code] = { code, plays, completions, best, last: entry };
  saveAll(map);
  return map[code];
}

/** The best grade earned for a mission, or null if it hasn't been recorded. */
export function getGrade(code) {
  if (!code) return null;
  return loadAll()[code]?.best?.grade ?? null;
}

/**
 * The best-scoring saved result for a mission (`{ score, grade, outcome, ... }`),
 * or null if it hasn't been recorded.
 */
export function getBest(code) {
  if (!code) return null;
  return loadAll()[code]?.best ?? null;
}

/** Wipe all saved mission data. */
export function clearAll() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing persisted / storage unavailable — nothing to clear.
  }
}
